/**
 * «Practicar estas N» of the end-of-game summary (plan 47): the corrections of
 * the game's «Casi» / «No era eso» attempts, practised with the coach's
 * correction review (`CoachReview`, the same one as the close and the daily
 * review; encargo 99), which needs the PERSISTED rows (ids + SRS state). The side-runner writes them live
 * (`language_session_corrections`), and the session summary route returns them
 * mid-session too (its `partial`), so the summary asks for them on tap and
 * keeps the ones of the game's turns.
 *
 * Pure: the component (`GamePractice.tsx`) fetches and opens the overlay.
 */
import { groupOverlappingCorrections, groupSamePhrase } from '@orborix/shared-types';
import type { SessionSummaryDistinctCorrection } from '../../lib/sessionSummaryApi.js';
import type { CoachReviewInput } from './assisted/CoachReview.js';
import type { GameSummaryReview } from './gameArena.js';

/** What the conversation lends the practice: whether the session is live, and its pause. */
export interface GamePracticeHost {
  /** The voice session is connected and running (not ended). */
  live: boolean;
  /** The learner already paused it (the practice then leaves it paused). */
  paused: boolean;
  pause: () => void;
  resume: () => void;
}

/** How many corrections the game's attempts showed: the N of «Practicar estas N». */
export function gamePracticeCount(review: ReadonlyArray<GameSummaryReview>): number {
  return review.reduce((n, r) => n + (r.turn ? r.corrections : 0), 0);
}

/** The sessions whose rows the practice needs (the live one, plus one resumed from). */
export function gamePracticeSessions(
  review: ReadonlyArray<GameSummaryReview>,
  liveSessionId: string,
): string[] {
  const out = new Set<string>();
  for (const r of review) {
    if (r.turn && r.corrections > 0) out.add(r.turn.sessionId ?? liveSessionId);
  }
  return [...out];
}

/**
 * The review cards: each persisted correction of a game attempt's turn, as the
 * close reviews it — none the learner discarded (thumbs down) nor marked
 * «ya lo sé». Those of the same turn that overlap are reviewed ONCE
 * (`groupOverlappingCorrections`, as in the close and the daily review): the
 * card carries every row of its group, so the result counts for all of them.
 */
export function gamePracticeItems(
  review: ReadonlyArray<GameSummaryReview>,
  rowsBySession: ReadonlyMap<string, ReadonlyArray<SessionSummaryDistinctCorrection>>,
  liveSessionId: string,
): CoachReviewInput[] {
  const picked: Array<{
    row: SessionSummaryDistinctCorrection;
    turn: string;
    said: string;
    corrected: string;
  }> = [];
  const taken = new Set<string>();
  for (const r of review) {
    if (!r.turn || r.corrections === 0) continue;
    const sessionId = r.turn.sessionId ?? liveSessionId;
    for (const row of rowsBySession.get(sessionId) ?? []) {
      if (row.turnIndex !== r.turn.turnIndex || taken.has(row.id)) continue;
      if (row.known || row.feedback?.vote === 'down') continue;
      taken.add(row.id);
      picked.push({
        row,
        turn: `${sessionId}:${row.turnIndex}`,
        said: row.userUtterance,
        corrected: row.correctedForm,
      });
    }
  }
  // Y las que terminan en la misma frase, aunque sean de turnos distintos
  // (Emmanuel, 20/10: «we use the train» y «we lose the train»): una tarjeta.
  return groupSamePhrase(
    groupOverlappingCorrections(picked, (x) => x.turn),
    ([head]) => head!.corrected,
  )
    .map((gs) => gs.flat())
    .map(([head, ...rest]) => ({
      id: head!.row.id,
      memberIds: [...head!.row.memberIds, ...rest.flatMap((x) => x.row.memberIds)],
      said: head!.row.userUtterance,
      corrected: head!.row.correctedForm,
      explanation: head!.row.explanation ?? null,
    }));
}
